import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { toast } from 'react-toastify';
import { setWithExpiry, getWithExpiry } from '../../helper/timeout_storage';
import { event as eventGTM } from '../../lib/gtag';
import { IYoutubeEmbed } from '../../interfaces/youtube';

import styles from './styles.module.scss';


const PREROLL_DURATION = 15;
const YOUTUBE_PREROLL = {
  showinfo: 0,
  controls: 0,
  disablekb: 1
}

const YoutubeEmbed = ({ 
  videoURL, playVideo = false, setPlayVideo, preRollURL, isPreroll = false,
  onPage, onPageClassName, autoPlay = true, 
  startedWatching, watchedVideo, handleVideoStart, handleVideoWatched
} : IYoutubeEmbed) => {
  const [videoID, setVideoID] = useState(""); // from youtube url
  const [prerollID, setPrerollID] = useState(""); // from youtube url

  const [playingVideo, setPlayingVideo] = useState(false)

  // As we need to play only one video, we should always create one player in order not to break it
  const [createdVideo, setCreatedVideo] = useState(false);

  const [processingURL, setProcessingURL] = useState(true);
  const [playedPreroll, setPlayedPreroll] = useState(false);

  const [trackVideoAnalytics, setTrackVideoAnalytics] = useState(false);

  const [isCounting, startCount] = useState(false);
  const [skipIn, setSkipIn] = useState(PREROLL_DURATION);

  const [displayedError, setDisplayedError] = useState(false)

  const [apiReady, setApiReady] = useState(false);

  const router = useRouter()

  const youtubeRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  
  useEffect(() => {
    // if the IFrame API is already loaded by <Script> on the page
    if (window.YT && (window.YT as any).loaded) {
      setApiReady(true);
      return;
    }
    // otherwise wait for the global callback
    window.onYouTubeIframeAPIReady = () => setApiReady(true);

    return () => {
      // clean up if unmounted before API ready
      delete window.onYouTubeIframeAPIReady;
    };
  }, []);


  // Retrieve videoID from URL on page load
  useEffect(() => {
    if (router.isReady && processingURL) {
      if (!!router.query.v) {
        setVideoID(`${router.query.v}`);
        setPlayVideo && setPlayVideo(true)
        setPlayedPreroll(true)
      }

      setProcessingURL(false)
    }
  }, [router, setPlayVideo, processingURL])
  
  // Managing videoID in URL
  useEffect(() => {
    if (router.isReady && !processingURL && !onPage) {
      
      if (videoID && !!!router.query.v) { // adding videoID to URL
        router.replace({
          pathname: router.pathname,
          query: { ...router.query, v: videoID }
        }, undefined, { shallow: true });
      }
      
      else if (videoID === "" && !!router.query.v) { // remove videoID from URL
        delete router.query.v;

        router.replace({
          pathname: router.pathname,
          query: { ...router.query }
        }, undefined, { shallow: true });
      }
    }
  }, [router, processingURL, videoID, onPage])

  // Handling video start event
  useEffect(() => {
    if (!handleVideoStart || startedWatching || !trackVideoAnalytics) return

    handleVideoStart()
  }, [trackVideoAnalytics, startedWatching, handleVideoStart])

  // Checking every second wether user watched the video
  useEffect(() => {
    if (watchedVideo || !handleVideoWatched || !playingVideo || !playedPreroll) return

    const interval = setInterval(() => {
      if (playerRef.current) {
        // if user watched 90% of the video, then it's considered as watched
        const watched = (playerRef.current.getCurrentTime() / playerRef.current.getDuration()) >= 0.9

        if (watched) {
          handleVideoWatched()
          clearInterval(interval)
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [watchedVideo, handleVideoWatched, playingVideo, playedPreroll, playerRef])


  const turnOffPlayer = useCallback(() => {
    if (youtubeRef && youtubeRef.current) {
      youtubeRef.current.classList.toggle(styles.youtubeDisappear);
    
      setTimeout(() => {
        const htmlDoc = document.children[0] as HTMLElement;
        htmlDoc.style.overflow = "";
        document.body.style.overflow = "";

        setPlayVideo && setPlayVideo(false);
        setDisplayedError(false);
        setCreatedVideo(false);
        setVideoID("");
      }, 500);  
    } 
  }, [setPlayVideo])

  
  // getting YouTubeVideoID from Youtube Video URL
  const getVideoID = useCallback((url: string, preroll = false) => {
    const id = url.split("?v=")[1]?.split("&")[0]

    // If the url to YouTube Video is in wrong format
    if (!id) {
      if (preroll) {
        setPlayedPreroll(true)
        console.error("Failed to play the preroll video", url)
      }
      else {
        turnOffPlayer()
        console.error("Failed to play the video", url)
        toast.error("The video is not available")
      }
      
      return ""
    }

    return id
  }, [turnOffPlayer])


  // Checking if preroll should be played
  useEffect(() => {
    if (!isPreroll) {
      setPlayedPreroll(true)
      return
    } 

    if (preRollURL && !playedPreroll) {
      // using local storage to check if the pre-roll has been played 2 hours before 
      if (!getWithExpiry("watched_preroll")) {
        setPrerollID(getVideoID(`${preRollURL}`, true))
      }
      else {
        setPlayedPreroll(true);
      }
    }
  }, [preRollURL, getVideoID, playedPreroll, isPreroll])

  
  // extracting YouTubeVideoID from Youtube Video URL
  useEffect(() => {
    if (playVideo && videoID === "") {
      setVideoID(getVideoID(videoURL))
    }
  }, [playVideo, videoID, getVideoID, videoURL])

  // skip preroll Timer
  useEffect(() => {
    if (isCounting && skipIn > 0 && playVideo && !playedPreroll) {
      const cancel = setTimeout(() => {
        setSkipIn(skipIn - 1);
      }, 1000);

      return () => clearTimeout(cancel);
    }
  }, [isCounting, skipIn, playVideo, playedPreroll])
  // reset timer just in case
  useEffect(() => {
    if (!playVideo) {
      setSkipIn(PREROLL_DURATION);
    }
  }, [playVideo])

  const onPrerollClick = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    window.open(preRollURL, "_blank")
  }
  const onSkipClick = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    if (skipIn <= 0) {
      setPlayedPreroll(true);
      setWithExpiry("watched_preroll", true, 2);
    }
    else {
      toast.success("Enjoy the Preroll")
    }
  }

  // Defines the behavior of the preroll
  const prerollStateChange = (event: any) => {
    switch(event?.data) {
      case 0: // if preroll ended
        setPlayedPreroll(true);
        setWithExpiry("watched_preroll", true, 2);
        break;
      case 1: // preroll is playing
        startCount(true)
        break;
    }

    if (event?.data === YT.PlayerState.PAUSED) {
      startCount(false)
    }
  }

  // Defines the behavior of the video
  const videoStateChange = (event: any) => {
    if (!event?.data) return
    
    // Tracking Video Playing
    if (event.data === YT.PlayerState.PLAYING) {
      setPlayingVideo(true)

      // Video Play Click should be track only if autoPlay is disabled
      eventGTM('videoPagePlayVideoClick')
      
      setTrackVideoAnalytics(true)
    }
    else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
      setPlayingVideo(false)
    }
  }

  useEffect(() => {
    if (!apiReady || !playVideo || videoID === "" || createdVideo) return;
  
    const failToPlayVideo = () => {
      if (!playedPreroll) {
        setPlayedPreroll(true);
        console.error("Failed to play the preroll video", prerollID);
      } else if (!displayedError) {
        setDisplayedError(true);
        toast.error("The video is not available");
        console.error("Failed to play the video", videoID);
        turnOffPlayer();
      }
    };
  
    const onReady = (event: any) => {
      if (autoPlay) {
        event.target.playVideo();
      }
    };
  
    const preRollSettings = playedPreroll ? {} : YOUTUBE_PREROLL;
  
    playerRef?.current?.destroy(); // stop any previous video
  
    try {
      playerRef.current = new YT.Player('player', {
        videoId: playedPreroll ? videoID : prerollID,
        playerVars: {
          mute: 0,
          ...preRollSettings
        },
        events: {
          'onStateChange': playedPreroll ? videoStateChange : prerollStateChange,
          'onError': failToPlayVideo,
          'onReady': onReady
        }
      });
  
      if (playedPreroll) setCreatedVideo(true);
    } catch (error) {
      console.error("Failed to initialize YouTube player:", error);
      failToPlayVideo();
    }
  }, [apiReady, playVideo, playedPreroll, prerollID, videoID, turnOffPlayer, displayedError, autoPlay, createdVideo]);
  

  
  if (onPage) {
    return (
      <div className={`${styles.youtubeOnPage} ${onPageClassName}`}>
        <div className={styles.youtubeVideo} onClick={!playedPreroll ? onPrerollClick : undefined}>
          <div id="player" />

          { !playedPreroll &&
            <div className={styles.onPagePrerollHolder}>
              <p className={`${styles.prerollText} ${skipIn === 0 && styles.skipText}`} onClick={onSkipClick}>
                { skipIn > 0 ? `Skip in ${skipIn}` : "Skip" }
              </p>
            </div>
          }
        </div>
      </div>
    )
  }

  if (!playVideo) return null;

  return (
    <div className={styles.youtubeHolder} onClick={turnOffPlayer} ref={youtubeRef}>
      <div className={`blurry-cover ${styles.cover}`}>&nbsp;</div>
      <div className={styles.youtubeVideo}>
        <div id="player" />
      </div>

      { !playedPreroll && (
        <div className={styles.playingPrerollHolder} onClick={onPrerollClick}>
          <p className={styles.prerollText} onClick={onSkipClick}>
            { skipIn > 0 ? `Skip in ${skipIn}` : "Skip" }
          </p>
        </div>
      )}
    </div>
  )
}

export default YoutubeEmbed;